import type { Roles } from '~/services/BasicServer/types'
import type {
  GlobalBizContract,
  GlobalBizContractStatus,
  GlobalBizRemoteAction,
} from '~/services/GlobalBizServer/types'

export const GLOBAL_BIZ_ORDER_STORAGE_KEY = 'globalBizPendingOrderNo'
export const GLOBAL_BIZ_ORDER_CREATED_EVENT = 'global-biz-order-created'
export const GLOBAL_BIZ_ALLOCATION_PROMPT_STORAGE_PREFIX = 'globalBizAllocationPromptSeen:'

const pendingStatuses = new Set<GlobalBizContractStatus>([
  'VERIFY_PENDING',
  'ACCOUNT_BINDING_PENDING',
  'ACCOUNT_BINDING_CONFLICT',
  'ALLOCATION_PENDING',
  'PROVISIONING',
  'PROVISION_FAILED',
  'MANUAL_PENDING',
  'REFUNDING',
])

const destructiveActions = new Set<GlobalBizRemoteAction>([
  'LEAVE_AND_CREATE_ENTERPRISE',
  'BIND_EXISTING_ENTERPRISE_WITH_OVERRIDE',
  'LOCAL_ENTITLEMENT_CONFLICT',
])

export function isGlobalBizTradeType(tradeType?: number | null): boolean {
  return tradeType !== null && tradeType !== undefined && tradeType >= 74 && tradeType <= 76
}

export function rememberGlobalBizOrder(orderNo?: string): void {
  if (!orderNo || typeof window === 'undefined') return
  window.localStorage.setItem(GLOBAL_BIZ_ORDER_STORAGE_KEY, orderNo)
  window.dispatchEvent(new CustomEvent(GLOBAL_BIZ_ORDER_CREATED_EVENT, { detail: { orderNo } }))
}

export function forgetGlobalBizOrder(): void {
  if (typeof window === 'undefined') return
  window.localStorage.removeItem(GLOBAL_BIZ_ORDER_STORAGE_KEY)
}

export function getRememberedGlobalBizOrder(): string {
  if (typeof window === 'undefined') return ''
  return window.localStorage.getItem(GLOBAL_BIZ_ORDER_STORAGE_KEY) || ''
}

export function getGlobalBizOpeningOrderNo(contract?: GlobalBizContract | null): string {
  return contract?.latestOrderNo || contract?.sourceOrderNo || ''
}

// 升档沿用合同，开通状态必须按新订单和周期隔离；分配版本会在本次确认时递增，不应作为缓存键。
export function getGlobalBizOpeningKey(contract?: GlobalBizContract | null): string {
  if (!contract?.contractNo) {
    const orderNo = getGlobalBizOpeningOrderNo(contract)
    return orderNo ? `order:${orderNo}` : ''
  }
  return `${contract.contractNo}:${contract.currentCycleNo}:${getGlobalBizOpeningOrderNo(contract)}`
}

export function isGlobalBizUpgrade(contract?: GlobalBizContract | null): boolean {
  return contract?.firstPurchaseFlag === 0 || Number(contract?.currentCycleNo || 0) > 1
}

export function getDefaultGlobalBizAllocation(totalQuota: number): { aiQuota: number; vipQuota: number } {
  const aiQuota = Math.floor(totalQuota / 2)
  return { aiQuota, vipQuota: totalQuota - aiQuota }
}

export function hasSeenGlobalBizAllocationPrompt(openingKey?: string): boolean {
  if (!openingKey || typeof window === 'undefined') return false
  return window.localStorage.getItem(`${GLOBAL_BIZ_ALLOCATION_PROMPT_STORAGE_PREFIX}${openingKey}`) === '1'
}

export function markGlobalBizAllocationPromptSeen(openingKey?: string): void {
  if (!openingKey || typeof window === 'undefined') return
  window.localStorage.setItem(`${GLOBAL_BIZ_ALLOCATION_PROMPT_STORAGE_PREFIX}${openingKey}`, '1')
}

export function forgetGlobalBizAllocationPrompt(openingKey?: string): void {
  if (!openingKey || typeof window === 'undefined') return
  window.localStorage.removeItem(`${GLOBAL_BIZ_ALLOCATION_PROMPT_STORAGE_PREFIX}${openingKey}`)
}

export function isGlobalBizOpeningStatus(status?: GlobalBizContractStatus): boolean {
  return Boolean(status && pendingStatuses.has(status))
}

export function globalBizRightsKey(planCode?: string): Roles | null {
  const mapping: Record<string, Roles> = {
    BASE: 'global_base' as Roles,
    PROFESSIONAL: 'global_middle' as Roles,
    ADVANCED: 'global_high' as Roles,
  }
  return planCode ? mapping[planCode] || null : null
}

export function getGlobalBizAccountEmail(userEmail?: string, buyerAccount?: string): string {
  return [userEmail, buyerAccount]
    .map(value => value?.trim() || '')
    .find(Boolean) || ''
}

export function requiresIrreversibleConsent(action?: GlobalBizRemoteAction): boolean {
  return Boolean(action && destructiveActions.has(action))
}

export function isValidGlobalBizAllocation(totalQuota: number, aiQuota: number, vipQuota: number): boolean {
  return Number.isInteger(totalQuota)
    && Number.isInteger(aiQuota)
    && Number.isInteger(vipQuota)
    && totalQuota > 0
    && aiQuota >= 0
    && vipQuota >= 0
    && aiQuota + vipQuota === totalQuota
}
